import React, { useEffect, useRef, useState } from "react";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRangePicker, createStaticRanges } from "react-date-range";
import "./datepicker.scss";
import moment from "moment";
import TickBlueIcon from "../../../../assets/tick-thin-blue.svg";
import TickIcon from "../../../../assets/tick-thin.svg";

export default function DatePicker({
  closeDatePicker,
  applyRange,
  rightButtonText,
  title,
  defaultDate,
  selectedRange,
  maxDate,
  minDate,
  customRangeLabel,
  handleOnChange,
  customStaticRange,
  showTickIcon,
}) {
  const today = new Date();
  const lastWeek = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 7
  );
  const lastWeekMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate()
  );
  const [selectionRange, setSelectionRange] = useState({
    startDate: defaultDate ? new Date(defaultDate) : new Date(),
    endDate: defaultDate ? new Date(defaultDate) : new Date(),
    dateDisplayFormat: "DD MMM YYYY",
    rangeColors: "#F969A4",
    color: "#F969A4",
    key: "selection",
  });

  const [focusedRange, setFocusedRange] = useState([0, 0]);

  const startRef = useRef(
    new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  );
  const endRef = useRef(new Date());

  const checkAllRange = (ranges) => {
    let todayTemp = moment(today, "MM/D/YYYY");
    let lastWeekTemp = moment(lastWeek, "MM/D/YYYY");
    let lastWeekMonthTemp = moment(lastWeekMonth, "MM/D/YYYY");
    let rangeStartDate = moment(ranges?.startDate, "MM/D/YYYY");
    let rangeEndDate = moment(ranges?.endDate, "MM/D/YYYY");

    if (
      todayTemp.isSame(rangeStartDate, "day") &&
      todayTemp.isSame(rangeEndDate, "day")
    ) {
      return "today";
    } else if (
      lastWeekTemp.isSame(rangeStartDate, "day") &&
      todayTemp.isSame(rangeEndDate, "day")
    ) {
      return "last 7 days";
    } else if (
      lastWeekMonthTemp.isSame(rangeStartDate, "day") &&
      todayTemp.isSame(rangeEndDate, "day")
    ) {
      return "last 30 days";
    } else {
      return "custom range";
    }
  };

  const createLabels = (label, filterLabel) => {
    return (
      <div style={{ display: "flex" }}>
        <img
          src={
            checkAllRange(selectionRange) === filterLabel
              ? TickBlueIcon
              : TickIcon
          }
          height={"16px"}
          width={"16px"}
        />
        <span style={{ marginLeft: "12px" }}>{label}</span>
      </div>
    );
  };

  const defaultStaticRanges = createStaticRanges([
    {
      label: showTickIcon ? createLabels("Today", "today") : "Today",
      range: () => ({
        startDate: today,
        endDate: today,
        dateFilterLabel: "today",
      }),
    },
    {
      label: showTickIcon
        ? createLabels("Last 7 Days", "last 7 days")
        : "Last 7 Days",
      range: () => ({
        startDate: lastWeek,
        endDate: today,
        dateFilterLabel: "last 7 days",
      }),
    },
    {
      label: showTickIcon
        ? createLabels("Last 30 Days", "last 30 days")
        : "Last 30 Days",
      range: () => ({
        startDate: lastWeekMonth,
        endDate: today,
        dateFilterLabel: "last 30 days",
      }),
    },
    {
      label: customRangeLabel ? customRangeLabel : "Custom Date Range",
      range: () => ({
        endDate: endRef.current,
        startDate: startRef.current,
        dateFilterLabel: "custom range",
      }),
    },
  ]);

  useEffect(() => {
    selectedRange && setSelectionRange(selectedRange);
    selectedRange && checkCustomRange(selectedRange);
  }, [selectedRange]);

  const handleSelect = (ranges) => {
    checkCustomRange(ranges);
    setSelectionRange(ranges);
    handleOnChange && handleOnChange(ranges);
  };

  const checkCustomRange = (ranges) => {
    let todayTemp = moment(today, "MM/D/YYYY");
    let lastWeekTemp = moment(lastWeek, "MM/D/YYYY");
    let lastWeekMonthTemp = moment(lastWeekMonth, "MM/D/YYYY");
    let rangeStartDate = moment(ranges?.startDate, "MM/D/YYYY");
    let rangeEndDate = moment(ranges?.endDate, "MM/D/YYYY");

    if (
      (todayTemp.isSame(rangeStartDate, "day") &&
        todayTemp.isSame(rangeEndDate, "day")) ||
      (lastWeekTemp.isSame(rangeStartDate, "day") &&
        todayTemp.isSame(rangeEndDate, "day")) ||
      (lastWeekMonthTemp.isSame(rangeStartDate, "day") &&
        todayTemp.isSame(rangeEndDate, "day"))
    ) {
      startRef.current = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1
      );
      endRef.current = today;
    } else {
      startRef.current = ranges.startDate;
      endRef.current = ranges.endDate;
    }
  };

  return (
    <div
      className={`appointment-date-range dashboard_position ${
        showTickIcon && "removeSelectedClass"
      }`}
    >
      {title && <div className="date-picker-title">{title}</div>}
      <DateRangePicker
        ranges={[selectionRange]}
        startDatePlaceholder="From"
        endDatePlaceholder="To"
        focusedRange={focusedRange}
        onRangeFocusChange={setFocusedRange}
        showSelectionPreview={true}
        editableDateInputs={true}
        showPreview={false}
        staticRanges={
          customStaticRange ? customStaticRange : defaultStaticRanges
        }
        inputRanges={[]}
        onChange={(item) => handleSelect(item.selection)}
        maxDate={maxDate ? maxDate : moment().add(1, "years").toDate()}
        minDate={minDate}
        moveRangeOnFirstSelection={false}
        retainEndDateOnFirstSelection={false}
        showDateDisplay={true}
      />
      <div className="d-flex col-sm-12 appointment-date-range-buttons-div p-2">
        <button
          className="offset-sm-8 appointment-date-range-cancel"
          onClick={closeDatePicker}
        >
          Cancel
        </button>
        <button
          className="ml-3 btn-primary appointment-date-range-apply"
          onClick={() => applyRange(selectionRange)}
        >
          {rightButtonText ? rightButtonText : "Apply"}
        </button>
      </div>
    </div>
  );
}
